/**
 * AssetCard — Sprint 18.5 reusable card for any tracked asset.
 * Asset-type agnostic: works for crypto, stocks, forex, commodities.
 *
 * Props (all optional unless marked required):
 *   symbol (required), name, category, tier, asset_type
 *   price_usd, change_24h, sparkline_data
 *   kairos_view: { predictions: [{direction, timeframe, confidence}],
 *                  recommendation: {action, conviction, reasoning_summary} | null,
 *                  hit_rate_30d: number | null }
 *   themes: [{name, stance, conviction}]
 *   catalysts: [...] (last 3 relevant)
 *   your_position: { holding_pct, target, note }
 *   detail_url: string (optional — for click-through)
 *   on_click: callback — opens detail modal
 *   show_tier_controls: bool — Sprint 19.5, opt-in inline tier pill + ×
 *                              (stocks only; /crypto uses different tier semantics)
 *   on_tier_changed: callback(tier) — fired after successful tier change
 *   on_removed: callback() — fired after successful archive
 */

import { useState } from 'react';
import { X, Check } from 'lucide-react';
import ConvictionSparkline from './ConvictionSparkline.jsx';

const TIER_COLORS = {
  dca_target:     { color: '#10b981', label: 'DCA' },
  tracked:        { color: '#3b82f6', label: 'TRACKED' },
  opportunistic:  { color: '#f59e0b', label: 'OPPORTUNISTIC' },
  core:           { color: '#10b981', label: 'CORE' },
  active:         { color: '#f59e0b', label: 'ACTIVE' },
  monitor:        { color: '#6b7280', label: 'MONITOR' },
};

// Sprint 19.5: stock tier pill semantic colors (Monitor gray, Active amber, Core green)
const STOCK_TIERS = [
  { key: 'monitor', label: 'Mon', full: 'Monitor', color: '#6b7280' },
  { key: 'active',  label: 'Act', full: 'Active',  color: '#f59e0b' },
  { key: 'core',    label: 'Core', full: 'Core',   color: '#10b981' },
];

const STANCE_COLORS = { bullish: '#10b981', bearish: '#ef4444', neutral: '#6b7280' };

function fmtPrice(p, assetType) {
  if (p == null) return '—';
  if (assetType === 'crypto' && p < 1) return `$${p.toFixed(p < 0.01 ? 6 : 4)}`;
  if (p < 100) return `$${p.toFixed(2)}`;
  return `$${p.toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
}

function fmtPct(x) {
  if (x == null) return '—';
  return `${x >= 0 ? '+' : ''}${x.toFixed(2)}%`;
}

export default function AssetCard({
  symbol, name, category, tier, asset_type = 'stock',
  price_usd, change_24h, sparkline_data,
  kairos_view = null, themes = [], catalysts = [], your_position = null,
  detail_url, on_click, onClick,
  show_tier_controls = false, on_tier_changed, on_removed,
}) {
  const handleClick = on_click || onClick || (detail_url ? () => { window.location.href = detail_url; } : undefined);
  const tierStyle = TIER_COLORS[tier] || TIER_COLORS.tracked;
  const changeColor = (change_24h || 0) >= 0 ? '#10b981' : '#ef4444';

  // Sprint 19.5: tier pill + remove state
  const [currentTier, setCurrentTier] = useState(tier);
  const [tierPending, setTierPending] = useState(false);
  const [toast, setToast] = useState(null);
  const [confirmRemove, setConfirmRemove] = useState(false);
  const [removed, setRemoved] = useState(false);

  async function changeTier(newTier, e) {
    e?.stopPropagation();
    if (newTier === currentTier || tierPending) return;
    const prev = currentTier;
    setCurrentTier(newTier); // optimistic
    setTierPending(true);
    try {
      const r = await fetch(`/api/watchlist/${symbol}`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ tier: newTier }),
      });
      if (!r.ok) throw new Error('update failed');
      setToast(`${symbol} → ${newTier.toUpperCase()}`);
      setTimeout(() => setToast(null), 1800);
      on_tier_changed?.(newTier);
    } catch {
      setCurrentTier(prev); // rollback
      setToast(`Failed — reverted to ${prev}`);
      setTimeout(() => setToast(null), 2400);
    } finally { setTierPending(false); }
  }

  async function removeTicker(e) {
    e?.stopPropagation();
    try {
      const r = await fetch(`/api/watchlist/${symbol}`, { method: 'DELETE' });
      if (!r.ok) throw new Error('remove failed');
      setRemoved(true);
      setToast(`${symbol} archived`);
      on_removed?.();
    } catch {
      setToast('Remove failed');
      setConfirmRemove(false);
    }
  }

  if (removed) return null; // hide immediately on successful remove

  const activePreds = kairos_view?.predictions || [];
  const topRec = kairos_view?.recommendation;
  const hitRate = kairos_view?.hit_rate_30d;

  const showControls = show_tier_controls && asset_type === 'stock';

  return (
    <div
      onClick={handleClick}
      className={`group relative bg-korinth-surface border border-korinth-border rounded-lg p-3 transition-all ${handleClick ? 'cursor-pointer hover:border-korinth-accent/60 hover:bg-korinth-bg/40 hover:shadow-lg' : ''}`}
    >
      {/* Toast */}
      {toast && (
        <div className="absolute top-1 right-1 z-10 px-2 py-0.5 rounded bg-korinth-accent text-black text-[9px] font-mono font-bold flex items-center gap-1">
          <Check className="w-2.5 h-2.5" /> {toast}
        </div>
      )}

      {/* Top row: symbol + name + badges */}
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-base font-mono font-bold text-white">{symbol}</span>
            {category && <span className="text-[9px] font-mono uppercase px-1 py-0.5 rounded bg-korinth-border text-korinth-muted">{category}</span>}
            {tier && !showControls && (
              <span className="text-[8px] font-mono font-bold uppercase px-1 py-0.5 rounded" style={{ color: tierStyle.color, backgroundColor: tierStyle.color + '15' }}>
                {tierStyle.label}
              </span>
            )}
          </div>
          {name && <div className="text-[10px] text-korinth-muted truncate">{name}</div>}
        </div>
        {showControls ? (
          <div className="flex items-center gap-1 flex-shrink-0" onClick={e => e.stopPropagation()}>
            {/* Tier pill — 3 segments */}
            <div className="flex rounded border border-korinth-border overflow-hidden opacity-80 group-hover:opacity-100 transition-opacity">
              {STOCK_TIERS.map(t => {
                const isActive = currentTier === t.key;
                return (
                  <button
                    key={t.key}
                    onClick={e => changeTier(t.key, e)}
                    disabled={tierPending}
                    title={`Set tier: ${t.full}`}
                    className={`text-[9px] font-mono font-bold px-1.5 py-0.5 transition-colors ${isActive ? '' : 'text-korinth-muted/70 hover:text-white hover:bg-korinth-border/50'}`}
                    style={isActive ? { color: '#000', backgroundColor: t.color } : {}}
                  >
                    {t.label}
                  </button>
                );
              })}
            </div>
            {/* Remove / confirm */}
            {!confirmRemove ? (
              <button
                onClick={e => { e.stopPropagation(); setConfirmRemove(true); }}
                title={`Remove ${symbol}`}
                className="text-korinth-muted/50 hover:text-korinth-danger opacity-0 group-hover:opacity-100 transition-opacity"
              >
                <X className="w-3 h-3" />
              </button>
            ) : (
              <div className="flex items-center gap-1 bg-korinth-danger/10 rounded px-1 py-0.5">
                <span className="text-[8px] font-mono text-korinth-danger">Remove?</span>
                <button onClick={removeTicker} className="text-[8px] font-mono text-korinth-accent hover:text-white font-bold">Y</button>
                <button onClick={e => { e.stopPropagation(); setConfirmRemove(false); }} className="text-[8px] font-mono text-korinth-muted hover:text-white">N</button>
              </div>
            )}
          </div>
        ) : (
          <ConvictionSparkline data={sparkline_data} stance="neutral" width={60} height={20} />
        )}
      </div>

      {/* Price + change */}
      <div className="flex items-center gap-2 mt-1.5">
        <span className="text-sm font-mono font-bold text-white">{fmtPrice(price_usd, asset_type)}</span>
        <span className="text-xs font-mono font-bold" style={{ color: changeColor }}>{fmtPct(change_24h)}</span>
      </div>

      {/* KAIROS inline row */}
      {(topRec || activePreds.length > 0 || hitRate != null) && (
        <div className="mt-1.5 flex items-center gap-2 flex-wrap text-[10px] font-mono">
          <span className="text-[9px] text-korinth-muted uppercase tracking-wider">KAIROS:</span>
          {topRec ? (
            <>
              <span className={topRec.action === 'buy' || topRec.direction === 'buy' ? 'text-korinth-accent font-bold' : topRec.action === 'sell' || topRec.direction === 'sell' ? 'text-korinth-danger font-bold' : 'text-korinth-warning font-bold'}>
                {(topRec.action || topRec.direction || '').toUpperCase()}
              </span>
              <span className="text-korinth-accent">{(topRec.conviction || topRec.conviction_overall)?.toFixed(1)}/10</span>
            </>
          ) : activePreds.length > 0 ? (
            <span className="text-korinth-text">
              {activePreds.length} prediction{activePreds.length > 1 ? 's' : ''} ({activePreds[0].direction} {activePreds[0].confidence}%)
            </span>
          ) : (
            <span className="text-korinth-muted italic">no active view</span>
          )}
          {hitRate != null && <span className="text-korinth-muted">· 30d hit {hitRate}%</span>}
        </div>
      )}

      {/* Themes inline */}
      {themes.length > 0 && (
        <div className="mt-1.5 flex items-center gap-1.5 flex-wrap text-[9px] font-mono">
          <span className="text-korinth-muted uppercase tracking-wider">Themes:</span>
          {themes.slice(0, 3).map(t => {
            const color = STANCE_COLORS[t.stance] || '#6b7280';
            const arrow = t.stance === 'bullish' ? '▲' : t.stance === 'bearish' ? '▼' : '→';
            return (
              <span key={t.name} className="flex items-center gap-0.5" style={{ color }}>
                {t.name} {arrow} {(t.conviction || 0).toFixed(1)}
              </span>
            );
          })}
          {themes.length > 3 && <span className="text-korinth-muted">+{themes.length - 3}</span>}
        </div>
      )}

      {/* Latest catalyst */}
      {catalysts.length > 0 && (
        <div className="mt-1.5 text-[9px] font-mono text-korinth-text truncate">
          <span className="text-korinth-muted">📰 </span>
          {catalysts[0].headline?.slice(0, 100) || catalysts[0]}
          {catalysts[0].source_credibility != null && (
            <span className="ml-1 text-korinth-muted">({Math.round(catalysts[0].source_credibility * 100)}%)</span>
          )}
        </div>
      )}

      {/* Your position */}
      {your_position && (your_position.target != null || your_position.holding_pct != null) && (
        <div className="mt-1.5 text-[9px] font-mono text-korinth-muted">
          {your_position.holding_pct != null && <>Holding {your_position.holding_pct}% · </>}
          {your_position.target != null && <>Target ${your_position.target}</>}
          {your_position.note && <> · {your_position.note}</>}
        </div>
      )}
    </div>
  );
}
